import { useEffect, useRef, useState } from 'react';
import { useHireExperts } from '@/context/HireExpertsContext';

/** Respects the visitor's motion preference for the scroll action. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Fraction of the page scrolled, clamped to 0 → 1. */
function scrollProgress(): number {
  const doc = document.documentElement;
  const max = doc.scrollHeight - doc.clientHeight;
  if (max <= 0) return 0;
  return Math.min(1, Math.max(0, window.scrollY / max));
}

/**
 * Floating "back to top" control. Appears after the visitor scrolls past the
 * fold and sits directly above the chat launcher, following whichever side the
 * launcher is set to (right or left) so the two always share a column. A thin
 * ring inside the button fills with the page's scroll progress.
 *
 * Hides while the chat panel is open so the two floating controls never
 * overlap, and steps above the sticky mobile action bar whenever that is on
 * screen (see `.cendien-back-to-top` in index.css).
 */
export function BackToTop() {
  const { chatOpen, launcherSide } = useHireExperts();
  const [visible, setVisible] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Write the ring progress straight to the DOM (no re-render per scroll frame).
  const applyProgress = () => {
    const el = btnRef.current;
    if (el) el.style.setProperty('--scroll-progress', String(scrollProgress()));
  };

  useEffect(() => {
    const onScroll = () => {
      applyProgress();
      setVisible(window.scrollY > 480);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // The button only mounts once visible — sync the ring the moment it appears.
  useEffect(() => {
    if (visible) applyProgress();
  }, [visible]);

  const handleClick = () => {
    try {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  };

  if (!visible || chatOpen) return null;

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={handleClick}
      data-side={launcherSide}
      aria-label="Back to top"
      title="Back to top"
      data-gi-cta="back-to-top"
      className="cendien-back-to-top w-12 h-12 flex items-center justify-center rounded-full bg-white border border-slate-300 text-slate-600 hover:text-teal-700 hover:border-teal-300 transition-colors cursor-pointer"
    >
      <span className="cendien-back-to-top-ring" aria-hidden="true"></span>
      <i className="ri-arrow-up-line text-xl relative z-10"></i>
    </button>
  );
}

export default BackToTop;